import React, {Component, useEffect, useRef, useState } from "react";
import GameInfo from "../Game/GameInfo.json"
import { useNavigate } from "react-router-dom";
import * as GameGUI from "../Game/GameGUI.js";
import * as camera_display from '../Game/Camera.js';
import { getRepetitionCount, setOnRepetitionCallback, ExerciseType, getFormScores, getRepHistory, cleanupExerciseDetection } from '../Exercise/ExerciseDetection';
import { cleanupPoseDetection } from '../Game/PoseDetection';
import { calculateSessionMetrics } from '../utils/SessionMetrics';
import Lottie from 'lottie-react';
import { Howler } from 'howler';
import useAnalytics from '../hooks/useAnalytics';
import "../Game/GameStyle.css"

const skip_intro = false

// Mapping between game types and exercise types
const gameToExerciseTypeMap = {
    "Apple Pick": ExerciseType.OVERHEAD_REACH,
    "Super Punch": ExerciseType.CROSS_BODY_PUNCH,
    "Hot Potato": ExerciseType.CORE_TWIST,
    "Tomato Dodge": ExerciseType.LATERAL_CORE_FLEXION,
    "Ocean Row": ExerciseType.ROW,
    "Sky Flight": ExerciseType.ARM_ABDUCTION,
    "Plant Care": ExerciseType.ARM_ABDUCTION_FRONT,
    "Berry Picking": ExerciseType.BICEP_CURL,
    "Soccer Kick": ExerciseType.SEATED_KNEE_EXTENSIONS,
    "Ice Skate": ExerciseType.SEATED_MARCHES
};

const GameArea = ({game_type})=>{
    const canvasRef = useRef(null)
    const cameraRef = useRef(null)
    const navigate = useNavigate();
    const isCleaningUpRef = useRef(false);
    const hasNavigatedRef = useRef(false);
    const resizeHandlerRef = useRef(null);
    const [gameModule, setGameModule] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [loadingAnimation, setLoadingAnimation] = useState(null)
    const { trackGameStart, trackGameComplete, trackGameAbandon } = useAnalytics();

    // CRITICAL FIX: Use useRef to maintain same game_state reference across renders
    // This ensures init_game() modifications are visible to BaseGame
    const gameStateRef = useRef({
        paused: true,
        loading: true
    });
    const game_state = gameStateRef.current;

    let rep = 0
    let max_rep = 999
    let max_timer = 180

    let loaded_game = GameInfo[game_type]
    const exerciseType = gameToExerciseTypeMap[game_type];

    // Debug logging
    console.log('🎮 GameArea loading:', {
        game_type,
        loaded_game: loaded_game ? loaded_game.module : 'NOT FOUND',
        exerciseType
    });

    useEffect(() => {
        const loadLottieAnimation = async () => {
            try {
                // Use Mendy animation as the loader instead of the rugby ball
                const response = await fetch('/dashboard/mendy-animation.json');
                if (response.ok) {
                    const animationData = await response.json();
                    setLoadingAnimation(animationData);
                }
            } catch (error) {
                console.error('Could not load Mendy animation:', error);
            }
        };
        
        loadLottieAnimation();
    }, []);

    /*
        SHOW INSTRUCTIONS
    */
    useEffect(() => {
        const loadGameModule = async ()=>{
            // OPTIMIZATION: Add initialization timeout to prevent infinite loading
            // WebView needs more time than regular Chrome browser for MediaPipe/WASM loading
            const isWebView = /wv|WebView/i.test(navigator.userAgent);
            const INIT_TIMEOUT = isWebView ? 45000 : 15000; // 45s for WebView, 15s for Chrome
            let timeoutId;

            console.log(`[GameArea] Environment: ${isWebView ? 'WebView' : 'Browser'}, Timeout: ${INIT_TIMEOUT}ms`);

            try{
                console.log(`[GameArea] Loading game module: ${loaded_game.module}`);
                const module = await import(`../Game/${loaded_game.module}.js`);
                setGameModule(module);

                if (cameraRef.current) {
                    // OPTIMIZATION: Timeout for camera initialization
                    console.log('[GameArea] Initializing camera display');
                    const cameraPromise = camera_display.init(module);
                    await Promise.race([
                        cameraPromise,
                        new Promise((_, reject) => {
                            timeoutId = setTimeout(() => {
                                reject(new Error('Camera initialization timed out. Your device may not support the required features.'));
                            }, INIT_TIMEOUT);
                        })
                    ]);
                    clearTimeout(timeoutId);

                    // OPTIMIZATION: Timeout for game module initialization
                    console.log('[GameArea] Initializing game module');
                    const gameInitPromise = module.init(window, game_state);
                    await Promise.race([
                        gameInitPromise,
                        new Promise((_, reject) => {
                            timeoutId = setTimeout(() => {
                                reject(new Error('Game initialization timed out'));
                            }, INIT_TIMEOUT);
                        })
                    ]);
                    clearTimeout(timeoutId);

                    console.log('[GameArea] ✅ Game fully initialized and ready');

                    document.getElementById("game-background").style.backgroundImage = `url(${loaded_game.background})`;

                    // CRITICAL FIX: Store resize handler to remove later
                    resizeHandlerRef.current = () => module.onWindowResize(window);
                    window.addEventListener('resize', resizeHandlerRef.current);
        
                    gui_set_game_info(loaded_game)
                    GameGUI.gui_set_callbacks(init_game, quit_game, pauseTimer, unpauseTimer)
                    GameGUI.gui_intro(skip_intro)

                    setOnRepetitionCallback(exerciseType, (count) => {
                        const rep_s = count.toString().padStart(2, '0');
                        const goalElement = document.getElementById("goal-text");
                        if (goalElement) {
                            goalElement.innerText = `${rep_s}x`;
                        }
        
                        const pauseGui = document.getElementById("pause-gui");
                        if (pauseGui && pauseGui.style.display !== 'none') {
                            GameGUI.setSummaryText(rep_s, max_timer - timer);
                        }
        
                        if (count >= max_rep) {
                            clearInterval(timer_interval);
                            GameGUI.gui_gameover(max_rep, max_timer);
                        }
                    });

                    setTimeout(() => {
                        game_state.loading = false;
                        setIsLoading(false);
                    }, 3000);
                }
            }
            catch (error){
                if (timeoutId) clearTimeout(timeoutId);

                console.error("Error loading game module:", error);
                console.error("Error stack:", error.stack);

                // OPTIMIZATION: Show user-friendly error message with troubleshooting steps
                const errorMsg = document.createElement('div');
                errorMsg.style.cssText = `
                    position: fixed;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    background: rgba(0,0,0,0.9);
                    color: white;
                    padding: 30px;
                    border-radius: 15px;
                    font-family: Arial, sans-serif;
                    z-index: 10000;
                    max-width: 90%;
                    max-height: 80%;
                    overflow-y: auto;
                    text-align: center;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.5);
                `;

                const errorType = error.message.includes('timeout') ?
                    'Initialization Timeout' : 'Initialization Failed';

                errorMsg.innerHTML = `
                    <h3 style="color: #ff6b6b; margin-bottom: 15px;">⚠️ ${errorType}</h3>
                    <p style="margin-bottom: 10px;">The game could not initialize properly.</p>
                    <p style="font-size: 14px; color: #ffd93d; margin-bottom: 15px;">
                        ${error.message}
                    </p>
                    <div style="background: rgba(255,255,255,0.1); padding: 15px; border-radius: 8px; margin: 15px 0; text-align: left;">
                        <p style="font-size: 13px; margin: 5px 0;"><strong>Possible causes:</strong></p>
                        <ul style="font-size: 12px; margin: 10px 0; padding-left: 20px; text-align: left;">
                            <li>Device GPU may not support WebGL/MediaPipe</li>
                            <li>Camera permissions not granted</li>
                            <li>Insufficient device memory or processing power</li>
                            <li>Browser compatibility issues</li>
                        </ul>
                        <p style="font-size: 13px; margin: 10px 0;"><strong>Try these solutions:</strong></p>
                        <ul style="font-size: 12px; margin: 10px 0; padding-left: 20px; text-align: left;">
                            <li>Ensure camera permissions are enabled</li>
                            <li>Close other apps to free up memory</li>
                            <li>Try using Chrome browser (latest version)</li>
                            <li>Refresh the page and try again</li>
                        </ul>
                    </div>
                    <div style="display: flex; gap: 10px; justify-content: center; flex-wrap: wrap;">
                        <button onclick="location.reload()" style="
                            padding: 12px 24px;
                            background: #4CAF50;
                            color: white;
                            border: none;
                            border-radius: 8px;
                            cursor: pointer;
                            font-size: 14px;
                            font-weight: bold;
                        ">🔄 Reload Page</button>
                        <button onclick="window.history.back()" style="
                            padding: 12px 24px;
                            background: #6c757d;
                            color: white;
                            border: none;
                            border-radius: 8px;
                            cursor: pointer;
                            font-size: 14px;
                            font-weight: bold;
                        ">← Go Back</button>
                    </div>
                    <p style="font-size: 11px; color: #888; margin-top: 15px;">
                        Technical details: ${error.stack ? error.stack.substring(0, 100) : 'N/A'}
                    </p>
                `;
                document.body.appendChild(errorMsg);

                setIsLoading(false);
            }
        }

        loadGameModule()
    }, [loaded_game])

    /*
        BEGIN GAME
    */
    const init_game = ()=>{
        console.log('🎮 init_game called! Unpausing game...');
        game_state.paused = false;
        startTimer(max_timer)
        
        // Track game start in analytics (minions branch approach)
        trackGameStart(game_type);
        console.log('📊 Analytics: trackGameStart called for', game_type);
    }



    /*
        INCREMENT REP AND END GAME
    */

    function get_rep_string(number){
        return number.toString().padStart(2, '0');
    }


    /*
        TIMER 
    */
    var timer_interval
    var timer
    function startTimer(duration) {

        timer = duration;
        timer_interval = setInterval(function () {
            GameGUI.gui_update_timer(timer)

            if (--timer < 0) {
                onTimerEnd()
            }
        }, 1000);
    }
    function pauseTimer(){
        game_state.paused = true
        GameGUI.setSummaryText(get_rep_string(getRepetitionCount(exerciseType)), max_timer - timer)
        clearInterval(timer_interval)
    }
    function unpauseTimer(){
        game_state.paused = false
        startTimer(timer)
    }

    /*On exercise end (GAME COMPLETE) */

    function onTimerEnd(){
        clearInterval(timer_interval)

        // Get final rep count
        const finalReps = getRepetitionCount(exerciseType);

        // NEW: Get per-rep history for session analytics
        const repHistory = getRepHistory(exerciseType);

        // Calculate session-wide metrics (weighted average, not recency-biased)
        const sessionMetrics = calculateSessionMetrics(repHistory);

        // Get patient ID for analytics
        const patientId = localStorage.getItem('patientId') || JSON.parse(localStorage.getItem('user') || '{}').id;

        // Calculate ROM display from average percentage
        const romPercentage = sessionMetrics.rom.average || 0;
        let romDisplay = 'Limited';
        if (romPercentage >= 90) romDisplay = 'Full range';
        else if (romPercentage >= 67) romDisplay = '¾ range';
        else if (romPercentage >= 45) romDisplay = '½ range';
        else if (romPercentage >= 22) romDisplay = '¼ range';

        trackGameComplete(game_type, undefined, {
            reps_completed: finalReps,
            reps_target: max_rep,
            session_duration_seconds: max_timer - timer,
            patient_id: patientId,
            exerciseType: game_type,

            // NEW: Session-averaged form score (not recency-biased)
            form_score: sessionMetrics.formScore,
            formScore: sessionMetrics.formScore, // Both keys for compatibility

            // NEW: Session-averaged ROM metrics
            rom_percentage: sessionMetrics.rom.average,
            rom: sessionMetrics.rom.average, // Both keys for compatibility
            rom_display: romDisplay,
            rangeOfMotion: sessionMetrics.rom.average,
            max_rom_achieved: sessionMetrics.rom.max,

            // NEW: Store detailed per-rep breakdown
            detailed_metrics: {
                per_rep_scores: repHistory,
                rom_consistency: sessionMetrics.rom.consistency,
                rom_trend: sessionMetrics.rom.trend,
                rom_median: sessionMetrics.rom.median
            },

            completion_type: 'timer_end',
            completionStatus: 'completed'
        });

        console.log('📊 Analytics: trackGameComplete called with SESSION metrics:', {
            formScore: sessionMetrics.formScore,
            romAverage: sessionMetrics.rom.average,
            romMax: sessionMetrics.rom.max,
            romConsistency: sessionMetrics.rom.consistency,
            romDisplay: romDisplay,
            reps: finalReps,
            perRepData: repHistory.length
        });

        GameGUI.show_gameover()
    }

    /*
        Quit game
    */

    // Cleanup function that handles cleanup without navigation
    const performCleanup = async () => {
        console.log('[GameArea] Starting cleanup sequence...');

        // 1. Stop timer first
        if (timer_interval) {
            clearInterval(timer_interval);
            timer_interval = null;
        }

        // 2. CRITICAL: Stop all audio FIRST (both Howler and ElevenLabs TTS)
        console.log('[GameArea] Stopping all audio');
        Howler.stop();
        
        // CRITICAL: Stop ElevenLabs TTS audio (HTML5 Audio + browser TTS)
        if (window.elevenLabsTTS) {
            window.elevenLabsTTS.stopAllAudio();
        }

        // 3. Cleanup game (before pose detection, so game stops using it)
        if (gameModule) {
            console.log('[GameArea] Cleaning up game module');
            if (typeof gameModule.cleanup === 'function') {
                gameModule.cleanup();
            }
            if (typeof gameModule.destroy === 'function') {
                gameModule.destroy();
            }
        }

        // 4. End exercise bridge session
        if (window.exerciseBridge) {
            if (typeof window.exerciseBridge.endSession === 'function') {
                window.exerciseBridge.endSession();
            }
        }

        // 5. CRITICAL: Cleanup exercise detection (closes PoseLandmarker)
        try {
            console.log('[GameArea] Cleaning up exercise detection');
            cleanupExerciseDetection();
        } catch (error) {
            console.warn('[GameArea] Error cleaning up exercise detection:', error);
        }

        // 6. CRITICAL: Cleanup pose detection (closes PoseLandmarker if separate instance)
        try {
            console.log('[GameArea] Cleaning up pose detection');
            cleanupPoseDetection();
        } catch (error) {
            console.warn('[GameArea] Error cleaning up pose detection:', error);
        }

        // 7. Remove resize listener to prevent leak
        if (resizeHandlerRef.current) {
            window.removeEventListener('resize', resizeHandlerRef.current);
            resizeHandlerRef.current = null;
            console.log('[GameArea] Resize listener removed');
        }

        // 8. Finally, close camera stream (closes ImageSegmenter last)
        console.log('[GameArea] Closing camera stream');
        await camera_display.close_stream(); // CRITICAL: Wait for async cleanup

        game_state.paused = true;
        game_state.loading = true;

        console.log('[GameArea] Cleanup sequence complete');
    };

    // Handle back button and page unload - cleanup BEFORE navigation
    useEffect(() => {
        const handlePopState = async (e) => {
            // Prevent duplicate cleanup attempts
            if (isCleaningUpRef.current) {
                window.history.pushState(null, '', window.location.pathname);
                return;
            }

            isCleaningUpRef.current = true;
            console.log('[GameArea] Back button pressed - preventing navigation until cleanup completes');

            // Prevent navigation while cleaning up
            window.history.pushState(null, '', window.location.pathname);

            try {
                // Complete cleanup while staying on current page
                await performCleanup();

                console.log('[GameArea] Cleanup complete - now navigating to dashboard');

                // Only navigate once
                if (!hasNavigatedRef.current) {
                    hasNavigatedRef.current = true;
                    navigate("/dashboard/patient");
                }
            } catch (error) {
                console.error('[GameArea] Cleanup error:', error);
                // Still navigate even if cleanup fails
                if (!hasNavigatedRef.current) {
                    hasNavigatedRef.current = true;
                    navigate("/dashboard/patient");
                }
            } finally {
                isCleaningUpRef.current = false;
            }
        };

        const handleBeforeUnload = async (e) => {
            await performCleanup();
        };

        window.addEventListener('popstate', handlePopState);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('popstate', handlePopState);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            // Reset refs on unmount
            isCleaningUpRef.current = false;
            hasNavigatedRef.current = false;
            // Non-blocking cleanup as backup
            performCleanup();
        };
    }, [navigate]);

    // Update quit_game to use the same cleanup logic plus audio cleanup
    async function quit_game(){
        // Track game abandonment in analytics with form scores (unless game completed)
        if (!game_state.loading && timer > 0) {
            const currentReps = getRepetitionCount(exerciseType);
            const formScores = getFormScores(exerciseType);
            
            // NEW: Get per-rep history for session analytics (same as onTimerEnd)
            const repHistory = getRepHistory(exerciseType);
            const sessionMetrics = calculateSessionMetrics(repHistory);
            
            // Get patient ID for analytics
            const patientId = localStorage.getItem('patientId') || JSON.parse(localStorage.getItem('user') || '{}').id;
            
            // Calculate ROM display from SESSION AVERAGE, not current ROM
            const romPercentage = sessionMetrics.rom.average || 0;
            let romDisplay = 'Limited';
            if (romPercentage >= 90) romDisplay = 'Full range';
            else if (romPercentage >= 67) romDisplay = '¾ range';
            else if (romPercentage >= 45) romDisplay = '½ range';
            else if (romPercentage >= 22) romDisplay = '¼ range';
            
            trackGameAbandon(game_type, undefined, {
                reps_completed: currentReps,
                reps_target: max_rep,
                session_duration_seconds: max_timer - timer,
                patient_id: patientId,
                exerciseType: game_type,
                
                // Form scores - use SESSION average, not current
                form_score: sessionMetrics.formScore,
                formScore: sessionMetrics.formScore,
                
                // ROM metrics - use SESSION average
                rom_percentage: sessionMetrics.rom.average,
                rom: sessionMetrics.rom.average,
                rom_display: romDisplay,
                rangeOfMotion: sessionMetrics.rom.average,
                max_rom_achieved: sessionMetrics.rom.max,
                
                // Detailed component scores
                accuracy_score: Math.round(formScores.accuracy || 0),
                accuracyScore: Math.round(formScores.accuracy || 0),
                control_score: Math.round(formScores.control || 0),
                controlScore: Math.round(formScores.control || 0),
                symmetry_score: Math.round(formScores.symmetry || 0),
                symmetryScore: Math.round(formScores.symmetry || 0),
                velocity_score: Math.round(formScores.velocity || 0),
                velocityScore: Math.round(formScores.velocity || 0),
                
                // NEW: Store detailed per-rep breakdown (same as onTimerEnd)
                detailed_metrics: {
                    per_rep_scores: repHistory,
                    rom_consistency: sessionMetrics.rom.consistency,
                    rom_trend: sessionMetrics.rom.trend,
                    rom_median: sessionMetrics.rom.median
                },
                
                completion_type: 'user_quit',
                completionStatus: 'abandoned'
            });
            
            console.log('📊 Analytics: trackGameAbandon called with SESSION metrics:', {
                formScore: sessionMetrics.formScore,
                romAverage: sessionMetrics.rom.average,
                romMax: sessionMetrics.rom.max,
                romDisplay,
                reps: currentReps,
                perRepData: repHistory.length
            });
        }

        // Wait for cleanup to complete before navigating
        await performCleanup();

        // performCleanup() already calls Howler.stop(), but call unload for extra cleanup
        Howler.unload();

        navigate("/dashboard/patient");
    }

    return(
        <div className="game-area-root">
            {isLoading && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000,
                    flexDirection: 'column'
                }}>
                    {loadingAnimation && (
                        <Lottie 
                            animationData={loadingAnimation}
                            style={{ width: 200, height: 200 }}
                            loop={true}
                        />
                    )}
                </div>
            )}

            <canvas id="game-background" style={{
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                backgroundPosition: "center",
                width: "100%",
                height: "100%",
                backgroundColor: "black",
                position: "absolute",
                zIndex: "-3"
            }} />

            <canvas id="gamecanvas" ref={canvasRef} style={{  width: '50%', height: '50%', backgroundColor: 'clear', position:'absolute', "zIndex":"-1"}} />
            <canvas id="camera_canvas" ref={cameraRef} style={{transform: "scale(1, 1)", width:"100%",height:"100%", backgroundColor: 'clear', position:'absolute', "zIndex":"-2"}} />
            <canvas id="game-foreground" style={{ visibility :"hidden", background:"url('2d-foreground.png')", backgroundRepeat:"no-repeat", backgroundSize:"cover", width: '100%', height: '100%', backgroundColor: 'clear', position:'absolute', "zIndex":"-1"   }} />

            <GameGUI.GameHUD />
        </div>
    )

}



export default GameArea